import { Spinner } from '@chakra-ui/react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { BasketballLoader } from 'components/BasketballLoader/BasketballLoader';
import { createContext, useContext, useEffect, useState } from 'react';
import {
  useLocation,
  Outlet,
  Navigate,
  useParams,
  useNavigate,
} from 'react-router-dom';
import { useGetEventByIdQuery } from '../app/api';
import { SportEventView } from '../types';

interface EventContextValue {
  currentEvent: SportEventView | null;
  setCurrentEvent: (event: SportEventView | null, navigate: boolean) => void;
}

const EventContext = createContext<EventContextValue>({
  currentEvent: null,
  setCurrentEvent: (event: SportEventView | null, navigate: boolean) => {},
});

export const useEventContext = () => useContext(EventContext);

export const EventContextProvider: React.FC = ({ children }) => {
  const navigate = useNavigate();
  const [currentEvent, setCurrentEvent] = useState<SportEventView | null>(null);

  const updateCurrentEvent = (
    event: SportEventView | null,
    shouldNavigate: boolean
  ) => {
    setCurrentEvent(event);
    if (event && shouldNavigate) {
      navigate(`/events/${event?.id}`);
    }
  };

  return (
    <EventContext.Provider
      value={{
        currentEvent,
        setCurrentEvent: updateCurrentEvent,
      }}
    >
      {children}
    </EventContext.Provider>
  );
};

export function RequireEvent() {
  const { eventId } = useParams();
  const { currentEvent, setCurrentEvent } = useEventContext();
  const location = useLocation();
  const { data, isLoading, error, isSuccess } = useGetEventByIdQuery(
    !currentEvent && eventId ? parseInt(eventId) : skipToken
  );

  useEffect(() => {
    if (!currentEvent && data) {
      setCurrentEvent(data, false);
    }
  }, [currentEvent, data, setCurrentEvent]);

  if (isLoading || (data && !currentEvent)) {
    return <BasketballLoader />;
  }

  return currentEvent && !error ? (
    <Outlet />
  ) : (
    <Navigate to="/" state={{ from: location }} />
  );
}
